import { UserDataRole } from '@/api/generated/models'
import { getAdminControllerFetchUsersQueryKey, useAdminControllerFetchUsers } from '@/api/generated/okami.ts'
import { Pagination } from '@/components/pagination.tsx'
import { Badge } from '@/components/ui/badge.tsx'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table.tsx'
import { ConfirmDeleteUserDialog } from '@/routes/_app/admin/-components/confirm-delete-user-dialog.tsx'
import { UpdateUserRoleDialog } from '@/routes/_app/admin/-components/update-user-role-dialog.tsx'
import { UserFilters } from '@/routes/_app/admin/-components/user-filters.tsx'
import { UsersTableActions } from '@/routes/_app/admin/-components/users-table-actions.tsx'
import { EmptyLoadingTable } from '@/routes/_app/syncs/-components/empty-loading-table.tsx'
import { CACHE_TIME, formatApiDate } from '@/utils/helpers.ts'
import { createFileRoute } from '@tanstack/react-router'
import { fallback, zodValidator } from '@tanstack/zod-adapter'
import { useMemo } from 'react'
import { z } from 'zod'

export const usersSearchParamsSchema = z.object({
  page: fallback(z.coerce.number().default(1), 1),
  limit: fallback(z.coerce.number().default(10), 10),
  search: fallback(z.string().optional().nullable(), null),
  role: fallback(z.enum(['ADMIN', 'USER', 'SUBSCRIBED_USER']).optional().nullable(), null),
})

export type UsersSearchParams = z.infer<typeof usersSearchParamsSchema>

export const getFetchUsersQueryKey = (searchParams: UsersSearchParams) => {
  return getAdminControllerFetchUsersQueryKey({
    page: searchParams.page,
    limit: searchParams.limit,
    search: searchParams.search ?? undefined,
    role: searchParams.role ?? undefined,
  })
}

export const roleMapperFormatter: Record<UserDataRole, string> = {
  ADMIN: 'Administrador',
  USER: 'Comum',
  SUBSCRIBED_USER: 'Assinante',
}

const badgeRoleVariant: Record<UserDataRole, 'default' | 'outline' | 'destructive'> = {
  ADMIN: 'destructive',
  USER: 'outline',
  SUBSCRIBED_USER: 'default',
}

export const Route = createFileRoute('/_app/admin/users')({
  component: UsersSectionPage,
  validateSearch: zodValidator(usersSearchParamsSchema),
})

function UsersSectionPage() {
  const searchParams = Route.useSearch()
  const navigate = Route.useNavigate()

  const { data: users, isLoading } = useAdminControllerFetchUsers(
    {
      page: searchParams.page,
      limit: searchParams.limit,
      search: searchParams.search ?? undefined,
      role: searchParams.role ?? undefined,
    },
    {
      query: {
        staleTime: CACHE_TIME.THIRTY_SECONDS,
      },
    },
  )

  const formattedUsers = useMemo(() => {
    return (
      users?.data.map((user) => ({
        ...user,
        formattedRole: roleMapperFormatter[user.role]?.toUpperCase(),
        createdAt: formatApiDate(user.createdAt, 'dd/MM/yyyy'),
        updatedAt: formatApiDate(user.updatedAt, 'dd/MM/yyyy') || 'Sem atualização',
      })) ?? []
    )
  }, [users?.data])

  return (
    <>
      <ConfirmDeleteUserDialog />
      <UpdateUserRoleDialog />

      <section className="flex flex-col gap-4">
        <h1 className="text-center text-3xl font-bold tracking-tighter md:text-left">Usuários</h1>

        <p className="text-center text-sm text-gray-500 md:text-left">
          Aqui você pode gerenciar os usuários do sistema, visualizar suas informações e realizar ações
          administrativas.
        </p>

        <UserFilters />

        <div className="rounded-sm border">
          <Table>
            <TableHeader>
              <TableHead>Identificador</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Tipo de Usuário</TableHead>
              <TableHead>Data de criação</TableHead>
              <TableHead>Ultima atualização</TableHead>
              <TableHead></TableHead>
            </TableHeader>
            <TableBody>{isLoading && <EmptyLoadingTable />}</TableBody>

            {formattedUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Badge variant="outline">
                    <code>{user.id}</code>
                  </Badge>
                </TableCell>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Badge variant={badgeRoleVariant[user.role]}>{user.formattedRole}</Badge>
                </TableCell>

                <TableCell>{user.createdAt}</TableCell>
                <TableCell>{user.updatedAt} </TableCell>
                <TableCell>
                  <UsersTableActions user={user} />
                </TableCell>
              </TableRow>
            ))}
          </Table>
        </div>

        <div className="flex flex-col items-center justify-between md:flex-row">
          <p className="text-sm text-muted-foreground ">
            Total de {(users?.totalOfPages ?? 1) * searchParams.limit} usuários encontrados
          </p>

          <div className="flex">
            <Pagination
              totalOfPages={users?.totalOfPages ?? 0}
              currentPage={searchParams.page}
              handlePageChange={(page) => navigate({ search: { page } })}
            />
          </div>
        </div>
      </section>
    </>
  )
}
